import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, request } from "../api/client";
import type { BasketLine } from "./commerceBasket";
import { parseSavedCart, type CartOrigin, type SavedCart } from "./savedCart";
import { cartLinePayload, parseCurrentCart, workingCartLines } from "./shoppingCart";

export function useShoppingCart(organizationId: string) {
  const [cart, setCart] = useState<SavedCart | null>(null), [lines, setLines] = useState<BasketLine[]>([]);
  const [loading, setLoading] = useState(true), [busy, setBusy] = useState(false), [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState<unknown>(), [ready, setReady] = useState(false);
  const pending = useRef<{ path: string; body: unknown; activateAfterSave?: boolean } | null>(null), running = useRef(false);
  const mounted = useRef(true), reads = useRef<AbortController | null>(null);
  const apply = useCallback((saved: SavedCart | null) => {
    const restored = saved ? workingCartLines(saved) : [];
    if (mounted.current) { setCart(saved); setLines(restored); setReady(true); }
  }, []);
  const refresh = useCallback(async () => {
    if (running.current || pending.current) return false;
    reads.current?.abort(); const controller = new AbortController(); reads.current = controller;
    setLoading(true); setError(undefined);
    try { const saved = parseCurrentCart(await request("/commerce/cart/current", "POST", {}, controller.signal), organizationId); if (!controller.signal.aborted) { apply(saved); return true; } return false; }
    catch (e) { if (!controller.signal.aborted) { setError(e); setReady(false); } return false; }
    finally { if (!controller.signal.aborted) setLoading(false); }
  }, [organizationId, apply]);
  useEffect(() => {
    mounted.current = true; void refresh();
    const changed = () => void refresh(); window.addEventListener("mzn:carts-changed", changed);
    return () => { mounted.current = false; reads.current?.abort(); window.removeEventListener("mzn:carts-changed", changed); };
  }, [refresh]);
  async function run(path?: string, body?: unknown, activateAfterSave = false) {
    if (running.current || (!pending.current && !path)) return;
    if (!pending.current) pending.current = { path: path!, body, activateAfterSave };
    reads.current?.abort(); running.current = true; setBusy(true); setLoading(false); setError(undefined);
    try {
      const operation = pending.current;
      let saved = parseSavedCart(await request(operation.path, "POST", operation.body), organizationId);
      if (operation.activateAfterSave) {
        // Keep the continuation durable in this retry: a lost save acknowledgement must still select the restored cart.
        pending.current = { path: "/commerce/cart/select", body: { requestId: crypto.randomUUID(), cartId: saved.cartId, revision: saved.revision } };
        saved = parseSavedCart(await request(pending.current.path, "POST", pending.current.body), organizationId);
      }
      apply(saved); pending.current = null;
      if (mounted.current) setUncertain(false);
      return saved;
    } catch (e) {
      const unknown = !(e instanceof ApiError) || e.status >= 500 || e.status === 408;
      if (!unknown) pending.current = null;
      if (mounted.current) { setError(e); setUncertain(unknown); }
    } finally { running.current = false; if (mounted.current) setBusy(false); }
  }
  const add = (line: BasketLine) => run("/commerce/cart/add", { requestId: crypto.randomUUID(), cartId: crypto.randomUUID(), ...cartLinePayload(line) });
  const save = (next: BasketLine[], origin: CartOrigin | null = cart) => {
    if (!origin) return Promise.resolve(undefined);
    return run("/commerce/cart/replaceDraft", { requestId: crypto.randomUUID(), cartId: origin.cartId, revision: origin.revision, lines: next.map(cartLinePayload) });
  };
  async function restore(next: BasketLine[], origin: CartOrigin) {
    // Save complete current choices before making an older draft the cross-device working cart.
    await run("/commerce/cart/replaceDraft", { requestId: crypto.randomUUID(), cartId: origin.cartId, revision: origin.revision, lines: next.map(cartLinePayload) }, true);
  }
  return { cart, lines, loading, busy, ready, uncertain, error, add, save, restore, refresh, retry: () => run() };
}
